'use client';

import { useEffect } from 'react';
import { FormProvider, useForm, type FieldValues } from 'react-hook-form';
import { Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { InheritedFormModeProvider } from '@/forms/shared/components/inherited-form-mode';
import type { HistoricFormData } from '@/lib/historic-form-loader';
import type { HistoricTaskInstance } from '@/lib/camunda/history-types';
import { useHistoricForm } from './use-historic-form';

interface ProcessHistoryHistoricFormProps {
  task: HistoricTaskInstance;
  formKey: string | null;
  taskId: string;
  processInstanceId: string;
  enabled: boolean;
  isStartForm?: boolean;
  onForbidden?: () => void;
}

function HistoricFormContent({
  taskId,
  processInstanceId,
  taskDefinitionKey,
  isStartForm,
  data,
}: {
  taskId: string;
  processInstanceId: string;
  taskDefinitionKey: string;
  isStartForm?: boolean;
  data: HistoricFormData;
}) {
  const { FormComponent, variables } = data;
  const form = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: variables,
  });

  useEffect(() => {
    form.reset(variables);
  }, [variables, form]);

  return (
    <FormProvider {...form}>
      <InheritedFormModeProvider fixedMode="historic">
        <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-900">
          <Info className="h-4 w-4" />
          <AlertTitle>Vista histórica</AlertTitle>
          <AlertDescription>
            {isStartForm
              ? 'Solo lectura. Se muestran las variables existentes al iniciar el proceso.'
              : 'Solo lectura. Se muestran las variables existentes al completarse esta tarea.'}
          </AlertDescription>
        </Alert>

        <div className="min-h-full pb-2">
          <FormComponent
            taskId={taskId}
            processInstanceId={processInstanceId}
            taskDefinitionKey={taskDefinitionKey}
            variables={variables}
            disabled
            hideAlerts
          />
        </div>
      </InheritedFormModeProvider>
    </FormProvider>
  );
}

export function ProcessHistoryHistoricForm({
  task,
  formKey,
  taskId,
  processInstanceId,
  enabled,
  isStartForm,
  onForbidden,
}: ProcessHistoryHistoricFormProps) {
  const { data, loading, error, reload } = useHistoricForm({
    task,
    formKey,
    processInstanceId,
    enabled,
  });

  useEffect(() => {
    if (error?.code === 'FORBIDDEN') {
      onForbidden?.();
    }
  }, [error?.code, onForbidden]);

  if (!enabled) return null;
  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (error) {
    if (error.code === 'FORBIDDEN') {
      return null;
    }
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
        <p className="text-destructive">
          {error.code === 'HISTORIC_FORM_UNSUPPORTED_VERSION'
            ? 'Este formulario pertenece a una versión antigua que ya no está disponible.'
            : error.message}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={reload}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <HistoricFormContent
      taskId={taskId}
      processInstanceId={processInstanceId}
      taskDefinitionKey={task.taskDefinitionKey}
      isStartForm={isStartForm}
      data={data}
    />
  );
}
